import { useEffect, useState } from 'react';
import { IxContentHeader, IxMessageBar, IxPill, IxTypography } from '@siemens/ix-react';
import { AuditService, ApiError } from '../api/client.js';
import type { ImportAuditRow } from '../api/client.js';
import { useIsAdmin } from '../auth/useIsAdmin.js';

// success -> success; error -> alarm; running -> info; cancelled -> neutral.
const STATUS_PILL_VARIANT: Record<string, 'success' | 'alarm' | 'info' | 'neutral'> = {
  success: 'success',
  error: 'alarm',
  running: 'info',
  cancelled: 'neutral',
};

// ---------------------------------------------------------------------------
// Shared cell styles
// ---------------------------------------------------------------------------

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  borderBottom: '2px solid var(--theme-color-ghost--20, rgba(128,128,128,0.25))',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.4rem 0.75rem',
  borderBottom: '1px solid var(--theme-color-ghost--10, rgba(128,128,128,0.15))',
  verticalAlign: 'top',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Audit() {
  const isAdmin = useIsAdmin();

  const [rows, setRows] = useState<ImportAuditRow[] | null>(null);
  // Start in the loading state: for admins the fetch fires on mount, so the
  // spinner should show immediately rather than flashing the empty heading for
  // one frame before the effect runs and flips loading true.
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin !== true) {
      return;
    }
    let cancelled = false;

    setLoading(true);
    AuditService.listAudits()
      .then((response) => {
        if (!cancelled) {
          setRows(response.rows ?? []);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          if (err instanceof ApiError && err.status === 403) {
            setFetchError(
              'Access denied (HTTP 403). You do not have permission to view audit data.',
            );
          } else if (err instanceof ApiError) {
            setFetchError(
              `Request failed (HTTP ${err.status}): ${err.message}`,
            );
          } else if (err instanceof Error) {
            setFetchError(err.message);
          } else {
            setFetchError('An unexpected error occurred.');
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  // ── Loading admin status ─────────────────────────────────────────────────
  if (isAdmin === undefined) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <IxTypography format="body">Checking permissions…</IxTypography>
      </div>
    );
  }

  // ── Not authorized ───────────────────────────────────────────────────────
  if (isAdmin === false) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <IxMessageBar type="warning">
          You are not authorized to view the import audit.
        </IxMessageBar>
      </div>
    );
  }

  // ── Admin: authorized ────────────────────────────────────────────────────
  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <IxContentHeader headerTitle="Import Audit" style={{ marginBottom: '1rem' }} />

      {/* BFF-level error (e.g. 403 despite UI check, network failure) */}
      {fetchError && (
        <IxMessageBar type="alarm" persistent style={{ marginBottom: '1rem' }}>
          {fetchError}
        </IxMessageBar>
      )}

      {loading && (
        <IxTypography format="body" style={{ marginBottom: '1rem' }}>
          Loading…
        </IxTypography>
      )}

      {!loading && rows !== null && rows.length === 0 && (
        <IxTypography format="body" textColor="soft">
          No recent imports.
        </IxTypography>
      )}

      {!loading && rows !== null && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <caption
              style={{
                textAlign: 'left',
                marginBottom: '0.5rem',
                fontWeight: 600,
              }}
            >
              Recent imports — {rows.length} row{rows.length !== 1 ? 's' : ''}
            </caption>
            <thead>
              <tr>
                <th scope="col" style={thStyle}>ID</th>
                <th scope="col" style={thStyle}>Filename</th>
                <th scope="col" style={thStyle}>File Type</th>
                <th scope="col" style={thStyle}>Status</th>
                <th scope="col" style={thStyle}>Started At</th>
                <th scope="col" style={thStyle}>Finished At</th>
                <th scope="col" style={thStyle}>Processed</th>
                <th scope="col" style={thStyle}>Rejected</th>
                <th scope="col" style={thStyle}>Error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-testid={`audit-row-${row.id}`}>
                  <td style={tdStyle}>{row.id}</td>
                  <td style={tdStyle}>{row.filename}</td>
                  <td style={tdStyle}>{row.fileType ?? '—'}</td>
                  <td style={tdStyle}>
                    <IxPill variant={STATUS_PILL_VARIANT[row.status] ?? 'neutral'}>
                      {row.status}
                    </IxPill>
                  </td>
                  <td style={tdStyle}>{row.startedAt ?? '—'}</td>
                  <td style={tdStyle}>{row.finishedAt ?? '—'}</td>
                  <td style={tdStyle}>{row.recordsProcessed ?? '—'}</td>
                  <td style={tdStyle}>{row.recordsRejected ?? '—'}</td>
                  <td
                    style={{
                      ...tdStyle,
                      color: row.error
                        ? 'var(--theme-color-alarm, #e00)'
                        : undefined,
                    }}
                  >
                    {row.error ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
